import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

// Designated centre gate is enforced upstream at basket
// selection. This config is only reachable at sites
// configured with isYellowFeverCentre: true and a valid
// yellowFeverCentreNumber.

registerTier2Config({
  pgdId: PGD_IDS.YELLOW_FEVER,
  displayName: "Yellow fever (STAMARIL\u00ae)",
  serviceType: "administration",
  minimumAgeYears: 1,
  indicationSummary:
    "Travelling to or through a yellow fever risk area, or requiring an International Certificate of Vaccination or Prophylaxis (ICVP) for entry. Designated Yellow Fever Vaccination Centre only. Protective immunity develops approximately 10 days after vaccination. The ICVP is valid for life from day 10.",

  brands: [
    {
      brandId: "stamaril",
      brandName: "STAMARIL\u00ae",
      minimumAgeYears: 1,
      route: "SC",
      doseDescription: "0.5 mL single dose (reconstituted)",
      administrationNotes:
        "Reconstitute the freeze-dried vaccine with the supplied solvent immediately before use. Shake gently until dissolved and inspect \u2014 the reconstituted solution should be clear and slightly pink to orange in colour. Use immediately after reconstitution. Do not allow contact with antiseptic or disinfectant on the syringe or skin \u2014 disinfectant contact may inactivate the live virus. Administer by subcutaneous injection. Intramuscular administration is only permitted where in line with official recommendations \u2014 document the route used and the justification. Never administer intravascularly. For patients with bleeding disorders, the subcutaneous route is preferred.",
    },
  ],

  schedules: [
    {
      scheduleId: "yellow-fever-single-dose",
      label: "Single dose \u2014 at least 10 days before entering an endemic area",
      offLabel: false,
      minimumAgeYears: 1,
      doses: [
        {
          doseNumber: 1,
          label: "Day 0 \u2014 single dose. ICVP valid from day 10.",
        },
      ],
      boosterInfo:
        "The ICVP is valid for life. Re-vaccination is only indicated for defined groups with reduced immunogenicity \u2014 refer to the current Green Book and NaTHNaC guidance for specific indications.",
      completionNotes:
        "Issue the ICVP (International Certificate of Vaccination or Prophylaxis) to the patient immediately using the physical certificate. The certificate is valid from day 10 after vaccination and is valid for life. Advise the patient not to enter a yellow fever risk area before day 10.",
    },
  ],

  capturesPriorDoseHistory: true,

  exclusions: [
    {
      checkId: "yf-ex-under-12-months",
      question: "Is the patient under 12 months of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Yellow fever vaccine is not licensed for patients under 12 months of age. Do not proceed.",
      referralMessage: "Refer to the patient's GP or a paediatric travel health specialist.",
    },
    {
      checkId: "yf-ex-hypersensitivity-components",
      question:
        "Does the patient have known hypersensitivity to any active substance, excipient, or production residual of STAMARIL\u00ae, including eggs or chicken proteins?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Yellow fever vaccine is contraindicated in this patient. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document. Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "yf-ex-latex",
      question:
        "Does the patient have known hypersensitivity to latex? (The STAMARIL\u00ae syringe tip caps contain a latex derivative.)",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "STAMARIL\u00ae syringe tip caps contain a latex derivative. This vaccine is contraindicated in patients with latex hypersensitivity. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document. Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "yf-ex-anaphylaxis-prior",
      question:
        "Has the patient had a severe hypersensitivity reaction or anaphylaxis following a previous dose of yellow fever vaccine?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A previous severe hypersensitivity reaction or anaphylaxis to yellow fever vaccine is a contraindication. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document. Refer to the patient's GP or an allergy specialist.",
    },
    {
      checkId: "yf-ex-thymus",
      question:
        "Does the patient have a history of thymus dysfunction (including myasthenia gravis or thymoma), or has the patient had a thymectomy?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "A history of thymus dysfunction or thymectomy is a contraindication to yellow fever vaccination. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document. Refer to the patient's GP.",
    },
    {
      checkId: "yf-ex-hiv",
      question:
        "Does the patient have symptomatic HIV infection, or asymptomatic HIV with evidence of impaired immune function?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Symptomatic HIV and asymptomatic HIV with impaired immune function are contraindications to yellow fever vaccination. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document. Refer to the patient's HIV specialist or GP.",
    },
    {
      checkId: "yf-ex-immunosuppressed",
      question:
        "Is the patient immunosuppressed due to congenital or acquired immune deficiency, high-dose systemic corticosteroids, immunosuppressive biological agents, radiotherapy, or cytotoxic treatment?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Immunosuppression is a contraindication to yellow fever vaccination as it is a live vaccine. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document. Refer to the patient's GP or specialist.",
    },
    {
      checkId: "yf-ex-pregnant",
      question: "Is the patient pregnant or could the patient be pregnant?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Yellow fever vaccine is contraindicated in pregnancy. Do not proceed. Women of childbearing potential who receive yellow fever vaccine should avoid pregnancy for at least 1 month after vaccination.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document. Refer to the patient's GP or obstetrician.",
    },
    {
      checkId: "yf-ex-breastfeeding-under-9-months",
      question: "Is the patient breastfeeding an infant who is under 9 months of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Yellow fever vaccine is contraindicated in patients breastfeeding an infant under 9 months of age. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry, do not vaccinate. Issue a medical exemption letter (designated centre only) and document.",
    },
    {
      checkId: "yf-ex-60-over-no-who-recommendation",
      question:
        "Is the patient aged 60 or over AND travelling to or through an area where WHO does NOT generally recommend yellow fever vaccination for the specific itinerary?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Yellow fever vaccination is not recommended by WHO for this patient's itinerary, and the patient is aged 60 or over. The increased risk of serious adverse events (YEL-AND and YEL-AVD) in this age group outweighs the benefit where WHO does not recommend vaccination. Do not proceed.",
      referralMessage:
        "If a yellow fever certificate is required for entry despite WHO not recommending vaccination for this itinerary, do not vaccinate. Issue a medical exemption letter (designated centre only) and document.",
    },
    {
      checkId: "yf-ex-occupational-only",
      question:
        "Is the patient's sole reason for vaccination an occupational risk from handling yellow fever virus or infected materials in a laboratory or clinical setting (i.e. no travel risk)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Vaccination for occupational risk from handling infectious materials is outside this PGD. Do not supply under this PGD.",
      referralMessage: "Direct the patient to their employer's occupational health service.",
    },
  ],

  cautions: [
    {
      checkId: "yf-caut-60-over-who-recommended",
      question:
        "Is the patient aged 60 or over AND travelling to or through an area where WHO recommends or advises yellow fever vaccination?",
      cautionMessage:
        "Patients aged 60 and over are at increased risk of serious adverse events following yellow fever vaccination, including yellow fever vaccine-associated neurological disease (YEL-AND) and yellow fever vaccine-associated viscerotropic disease (YEL-AVD). Before proceeding, a documented individual risk-benefit discussion must be completed and recorded. Only vaccinate where the patient is travelling to an area where WHO recommends or advises vaccination for the specific itinerary. Ensure the patient is counselled on the symptoms of YEL-AND and YEL-AVD (see patient advice).",
    },
    {
      checkId: "yf-caut-breastfeeding-9-months-over",
      question: "Is the patient breastfeeding an infant aged 9 months or over?",
      cautionMessage:
        "Yellow fever vaccine may be given to patients breastfeeding an infant aged 9 months or over. However, breastfeeding must be interrupted for at least 2 weeks after vaccination. Advise the patient to express and discard breast milk during this 2-week period and use formula or stored breast milk to feed the infant. Document this advice was given.",
    },
    {
      checkId: "yf-caut-temporary-immunosuppression",
      question:
        "Has the patient recently completed a course of systemic corticosteroids lasting 14 days or more, or any other temporary immunosuppressive treatment?",
      cautionMessage:
        "Delay yellow fever vaccination until at least 1 month after completing a systemic corticosteroid course of 14 days or longer, or until at least 1 month after any other temporary immunosuppressive treatment is complete. If the patient is no longer immunosuppressed and the 1-month interval has elapsed, vaccination may proceed. Document the interval and the clinical decision.",
    },
    {
      checkId: "yf-caut-childbearing-potential",
      question: "Is the patient a woman of childbearing potential who is not currently pregnant?",
      cautionMessage:
        "Women of childbearing potential should be advised to avoid pregnancy for at least 1 month after yellow fever vaccination. Document that this advice was given.",
    },
    {
      checkId: "yf-caut-bleeding",
      question: "Does the patient have a bleeding disorder or is the patient on anticoagulant therapy?",
      cautionMessage:
        "For patients with bleeding disorders, the subcutaneous route is preferred for yellow fever vaccination. Apply firm pressure after injection. For patients on stable warfarin with INR within therapeutic range, the subcutaneous injection may proceed using a fine-gauge needle. Document the route used and the clinical decision.",
    },
    {
      checkId: "yf-caut-yelav-counsel",
      question: "Has the pharmacist reviewed the YEL-AND and YEL-AVD risk counselling requirement for this patient?",
      cautionMessage:
        "All patients receiving yellow fever vaccine must be counselled on the symptoms of yellow fever vaccine-associated neurological disease (YEL-AND) and yellow fever vaccine-associated viscerotropic disease (YEL-AVD). YEL-AND symptoms: high fever with severe headache, confusion, personality change, neck stiffness, fits, or limb weakness \u2014 seek urgent medical attention. YEL-AVD symptoms: fever, muscle pain, fatigue, headache, low blood pressure \u2014 seek urgent medical attention. In both cases, the clinician must be informed that yellow fever vaccine was recently given. Document that this counselling was provided.",
    },
    {
      checkId: "yf-caut-syncope",
      question: "Is there any history of syncope (fainting) following a previous injection or vaccination?",
      cautionMessage:
        "Syncope can occur following vaccination. Ensure the patient is seated or lying down during and for the duration of the observation period after administration.",
    },
  ],

  supplyGates: [],

  requiresICVP: true,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: true,
  requiresGPNotification: true,
  standardObservationMinutes: 15,
  extendedObservationMinutes: 30,

  patientAdvicePoints: [
    "This vaccine protects against yellow fever. Protective immunity develops approximately 10 days after vaccination \u2014 do not enter a yellow fever risk area before day 10.",
    "Your International Certificate of Vaccination or Prophylaxis (ICVP) is valid for life from day 10 after vaccination. Keep this certificate safe as it may be required for entry to certain countries.",
    "If you are a woman of childbearing potential: avoid pregnancy for at least 1 month after this vaccination.",
    "If you are breastfeeding an infant aged 9 months or over: do not breastfeed for at least 2 weeks after vaccination. Express and discard breast milk during this period and use formula or stored breast milk to feed your infant.",
    "Yellow fever vaccine-associated neurological disease (YEL-AND): seek urgent medical attention if you develop high fever with severe headache, confusion, personality change, stiff neck, fits, or weakness in the arms or legs after vaccination. Tell the clinician you have recently received yellow fever vaccine.",
    "Yellow fever vaccine-associated viscerotropic disease (YEL-AVD): seek urgent medical attention if you develop fever, muscle pain, fatigue, headache, or low blood pressure after vaccination. Tell the clinician you have recently received yellow fever vaccine.",
    "Continue to use mosquito-bite avoidance measures throughout your trip: apply insect repellent containing DEET to exposed skin, wear long-sleeved clothing and long trousers, and use permethrin-treated clothing and bed nets. The vaccine reduces but does not eliminate risk.",
    "If you develop fever, jaundice, or any serious illness during or after travel in a yellow fever risk area, seek urgent medical attention and inform the clinician of your travel history and yellow fever vaccination.",
  ],
});
